import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "cyber-compliance",
  kind: "horizontal",
  title: "Cyber Compliance & Audit Automation",
  shortTitle: "Cyber Compliance",
  status: "draft",
  tier: "experience",
  seoTitle: "Cyber Compliance & Audit Automation | General Applications",
  seoDescription:
    "Automated control mapping, audit evidence collection, and regulatory alignment across NIST 800-53, CMMC, HIPAA, HITRUST, and NERC CIP.",
  summary:
    "Control mapping and evidence systems that organize technical facts once, then make them reviewable across the standards, frameworks, and operating environments that apply.",
  image: "/images/blog/reconciling-overlapping-security-frameworks-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["military", "enterprise", "energy", "healthcare"],
  relatedPostSlugs: [
    "reconciling-overlapping-security-frameworks",
    "grounded-ai-cyber-compliance",
  ],
  engineering: [
    "Technical-control mapping to multiple standards and frameworks",
    "Audit-evidence collection from configuration, asset, access, and security telemetry",
    "Multi-framework control crosswalks that retain the technical source of each assertion",
    "Authorization, assessment, and audit traceability records",
    "Evidence models for device, network, cryptographic, and cloud controls",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Standards and Requirements by Operating Environment",
    },
    {
      type: "paragraph",
      text: "The work is not treating every framework as a separate paperwork exercise. It is establishing the technical facts first—asset identity, configuration, access, boundary, change, and evidence—then mapping those facts to the requirements that apply. The public standards and requirements below are grouped by the environments where that work has been performed.",
    },
    {
      type: "heading",
      level: 2,
      text: "Defense and Federal Systems",
    },
    {
      type: "list",
      items: [
        "**NIST SP 800-53 and NIST Cybersecurity Framework:** Control mapping across Rev. 4, Rev. 5, and CSF 2.0.",
        "**DoD Risk Management Framework:** Authorization-package and control-traceability work under DoDI 8510.01 and CNSSI 1253.",
        "**DISA STIGs and SRGs:** Technical baseline and assessment evidence aligned to Security Technical Implementation Guides and Security Requirements Guides.",
        "**CMMC and NIST SP 800-171 / 800-172:** Controlled Unclassified Information protection and assessment evidence.",
        "**FedRAMP and DoD Cloud Computing SRG:** Cloud-control evidence across Moderate, High, and IL4, IL5, and IL6 environments.",
        "**ICD 503:** Intelligence-community security-control traceability.",
      ],
    },
    {
      type: "heading",
      level: 2,
      text: "Energy and Critical Infrastructure",
    },
    {
      type: "list",
      items: [
        "**NERC CIP:** Critical-infrastructure compliance across CIP-002 through CIP-014, including Reliability Standard Audit Worksheet preparation.",
        "**NRC 10 CFR 73.54:** Digital instrumentation and control cybersecurity evidence for nuclear-generation environments.",
      ],
    },
    {
      type: "heading",
      level: 2,
      text: "Healthcare and Life Sciences",
    },
    {
      type: "list",
      items: [
        "**HIPAA Security and Privacy Rules:** Safeguard architecture for electronic protected health information (ePHI), access controls, audit trail logging, and Business Associate Agreement (BAA) boundaries.",
        "**HITRUST CSF:** Common Security Framework control mapping, certification evidence collection, and shared-responsibility matrices across cloud and clinical systems.",
      ],
    },
    {
      type: "heading",
      level: 2,
      text: "Enterprise and Assurance Controls",
    },
    {
      type: "list",
      items: [
        "**FIPS 140-2 and FIPS 140-3:** Cryptographic-module verification and transit-encryption review.",
        "**PCI DSS and SOC 2 Type II:** Control mapping from operational telemetry to payment-security and assurance evidence.",
      ],
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the applicable requirements and the technical sources that can prove or disprove each control before assembling evidence.",
      produces: [
        "Regulatory applicability and control-overlap matrix",
        "Evidence-source inventory across devices, networks, identities, and cloud systems",
        "Gap analysis between technical state and required assertions",
        "Audit-scope and evidence-ownership model",
      ],
      fit: {
        good: [
          "Organizations maintaining overlapping frameworks against the same technical environment",
          "Teams whose evidence is scattered across configuration exports, spreadsheets, tickets, and manual screenshots",
        ],
        poor: [
          "A request for a compliance certification without access to the supporting technical systems",
          "A requirement to substitute generic policy text for verifiable technical evidence",
        ],
      },
    },
    "initial-design": {
      focus:
        "Model technical assertions once and define their projections into the requirements that govern the environment.",
      produces: [
        "Canonical technical-control and evidence schema",
        "Framework crosswalk and control-projection model",
        "Evidence-collection and refresh architecture",
        "Traceability design from control to source system",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the evidence checks, control tests, and review paths that make the model usable during an assessment.",
      produces: [
        "Control-specific evidence queries and collection procedures",
        "Assessment, exception, and remediation workflow definitions",
        "Audit-package and traceability templates",
        "Human-review gates for automated evidence interpretation",
      ],
    },
    implementation: {
      focus:
        "Build the evidence and crosswalk mechanisms around real technical systems, then validate them with the people responsible for operations and audit defense.",
      produces: [
        "Automated and reviewable evidence collection",
        "Multi-framework control mappings grounded in source telemetry",
        "Exception and remediation records linked to the affected control",
        "Assessment-ready traceability packages",
      ],
    },
  },
};
